import type { ApiErrorBody, ErrorCode } from '@bifeai/shared';

const BASE = `${import.meta.env.VITE_API_URL ?? ''}/api/v1`;

/** Error de la API con su código, mensaje en español y errores por campo. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode,
    message: string,
    readonly requestId: string | null,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let lastNetwork = true;
function reportNetwork(ok: boolean): void {
  if (ok === lastNetwork || typeof window === 'undefined') return;
  lastNetwork = ok;
  window.dispatchEvent(new Event(ok ? 'bifeai:online' : 'bifeai:offline'));
}

let selectedBranch: string | null = null;
/** La sucursal elegida en el selector viaja en cada request (solo la usa la casa central). */
export function setApiBranch(id: string | null): void {
  selectedBranch = id;
}

type Json = Record<string, unknown> | unknown[];

async function request<T>(method: string, path: string, body?: Json): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (selectedBranch) headers['X-Branch-Id'] = selectedBranch;

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    // El punto de venta escucha esto para seguir vendiendo sin internet (ver lib/offline.ts).
    reportNetwork(false);
    throw new ApiError(0, 'INTERNAL_ERROR', 'No hay conexión con el servidor. Revisá internet y probá de nuevo.', null);
  }
  reportNetwork(true);

  if (res.status === 204) return undefined as T;
  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (data as ApiErrorBody | null)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'INTERNAL_ERROR',
      err?.message ?? 'Ocurrió un error inesperado.',
      err?.requestId ?? res.headers.get('X-Request-Id'),
      err?.fields,
    );
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: Json) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: Json) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: Json) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

export function isApiError(e: unknown, ...codes: ErrorCode[]): e is ApiError {
  return e instanceof ApiError && (codes.length === 0 || codes.includes(e.code));
}

/** Pasa los errores por campo de la API a react-hook-form. */
export function applyFieldErrors(err: unknown, setError: (name: never, e: { message: string }) => void): boolean {
  if (!(err instanceof ApiError) || !err.fields) return false;
  for (const [name, message] of Object.entries(err.fields)) setError(name as never, { message });
  return true;
}

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Ocurrió un error inesperado.';
}

/**
 * Sube un archivo directo al almacenamiento con la URL firmada que entrega la API.
 * En producción va a Cloudflare R2 sin pasar por nuestro servidor; en desarrollo, a la API local.
 */
export async function uploadToTicket(ticket: { uploadUrl: string; headers: Record<string, string> }, blob: Blob): Promise<void> {
  const absolute = /^https?:\/\//.test(ticket.uploadUrl);
  let res: Response;
  try {
    res = await fetch(absolute ? ticket.uploadUrl : `${import.meta.env.VITE_API_URL ?? ''}${ticket.uploadUrl}`, {
      method: 'PUT',
      headers: ticket.headers,
      body: blob,
      credentials: 'omit',
    });
  } catch {
    throw new ApiError(0, 'INTERNAL_ERROR', 'No se pudo subir la foto. Revisá internet y probá de nuevo.', null);
  }
  if (!res.ok) throw new ApiError(res.status, 'INTERNAL_ERROR', 'No se pudo subir la foto. Probá de nuevo.', null);
}

/** Descarga un archivo de la API (por ejemplo, "Descargar mis datos") y lo guarda en la computadora. */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { credentials: 'include' });
  } catch {
    throw new ApiError(0, 'INTERNAL_ERROR', 'No hay conexión con el servidor. Revisá internet y probá de nuevo.', null);
  }
  if (!res.ok) {
    const err = ((await res.json().catch(() => null)) as ApiErrorBody | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'INTERNAL_ERROR', err?.message ?? 'No se pudo descargar.', err?.requestId ?? null);
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
